import { Directive, Input, OnInit, inject } from '@angular/core';

import { CasinoTheme, FeatureNames, FeatureUIConfigMap, UIElement } from '@frontend/casino-ui/shared';
import { DsButtonIcon, DsButtonIconKind, DsButtonIconVariant } from '@frontend/ui/button-icon';
import { DsNotificationBubble, DsNotificationBubbleVariant } from '@frontend/ui/notification-bubble';
import { DsPaginationIndicator } from '@frontend/ui/pagination-indicator';
import { DsPill, DsPillVariant } from '@frontend/ui/pill';
import { DsSegmentedControl } from '@frontend/ui/segmented-control';

import { themeConfig } from './themeAppearanceConfig';

@Directive({
    selector: '[csAppearanceThemeModifier]',
    standalone: true,
})
export class AppearanceThemeModifierDirective implements OnInit {
    @Input() elementType?: UIElement;
    @Input() featureName?: FeatureNames;
    @Input() currentTheme: CasinoTheme = 'bwin';

    private pill = inject(DsPill, { optional: true, host: true });
    private notificationBubble = inject(DsNotificationBubble, { optional: true, host: true });
    private buttonIcon = inject(DsButtonIcon, { optional: true, host: true });
    private segmentedControl = inject(DsSegmentedControl, { optional: true, host: true });
    private paginationIndicator = inject(DsPaginationIndicator, { optional: true, host: true });

    private config!: FeatureUIConfigMap;

    ngOnInit(): void {
        this.config = themeConfig?.[this.currentTheme] || {};
        this.applyConfigSettings();
    }

    applyConfigSettings(): void {
        if (!this.featureName || !this.elementType) return;

        const configMap: Record<string, () => void> = {
            pill: this.applyPillConfig.bind(this),
            notificationBubble: this.applyNotificationBubbleConfig.bind(this),
            buttonIcon: this.applyButtonIconConfig.bind(this),
            segmentedControl: this.applySegmentedControlConfig.bind(this),
            paginationIndicator: this.applyPaginationIndicatorConfig.bind(this),
        };

        const applyConfig = configMap[this.elementType];
        if (applyConfig) {
            applyConfig();
        }
    }

    private applyNotificationBubbleConfig(): void {
        if (!this.notificationBubble || !this.featureName || !this.elementType) return;

        const bubbleConfig = this.config?.[this.featureName]?.[this.elementType];

        if (bubbleConfig) {
            this.notificationBubble.inverse.set(!!bubbleConfig.inverse);
            if (bubbleConfig.variant) this.notificationBubble.variant.set(bubbleConfig.variant as DsNotificationBubbleVariant);
        }
    }

    private applyButtonIconConfig(): void {
        if (!this.buttonIcon || !this.featureName || !this.elementType) return;
        const buttonIconConfig = this.config?.[this.featureName]?.[this.elementType];
        if (buttonIconConfig) {
            this.buttonIcon.inverse.set(!!buttonIconConfig.inverse);
            if (buttonIconConfig.variant) this.buttonIcon.variant.set(buttonIconConfig.variant as DsButtonIconVariant);
            if (buttonIconConfig.kind) this.buttonIcon.kind.set(buttonIconConfig.kind as DsButtonIconKind);
        }
    }

    private applyPillConfig(): void {
        if (!this.pill || !this.featureName || !this.elementType) return;
        const pillConfig = this.config?.[this.featureName]?.[this.elementType];
        if (pillConfig) {
            this.pill.inverse.set(!!pillConfig.inverse);
            if (pillConfig.variant) this.pill.variant.set(pillConfig.variant as DsPillVariant);
        }
    }

    private applySegmentedControlConfig(): void {
        if (!this.segmentedControl || !this.featureName || !this.elementType) return;

        const segmentedControlConfig = this.config?.[this.featureName]?.[this.elementType];

        if (segmentedControlConfig) {
            this.segmentedControl.inverse.set(!!segmentedControlConfig.inverse);
        }
    }

    private applyPaginationIndicatorConfig(): void {
        if (!this.paginationIndicator || !this.featureName || !this.elementType) return;
        const config = this.config?.[this.featureName]?.[this.elementType];
        if (config) {
            this.paginationIndicator.inverse.set(!!config.inverse);
        }
    }
}
